"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import DirectionalArrow from "@/components/ui/DirectionalArrow";
import DirectionalExternalArrow from "@/components/ui/DirectionalExternalArrow";
import { imageSrc } from "@/lib/constants";
import { ALL_INTEGRATION_CATEGORY } from "@/lib/content/integrations/config";
import type { IntegrationItem } from "@/lib/content/integrations";
import type { Locale } from "@/lib/i18n/config";
import { useLocale } from "@/lib/i18n/locale-context";
import { localizeHref } from "@/lib/i18n/routing";
import { cn } from "@/lib/utils";

export default function IntegrationsCatalog({
  items,
  categories,
}: {
  items: IntegrationItem[];
  categories: string[];
}) {
  const locale = useLocale();
  const [category, setCategory] = useState(ALL_INTEGRATION_CATEGORY);

  const visible = useMemo(() => {
    if (category === ALL_INTEGRATION_CATEGORY) {
      return items;
    }

    return items.filter((item) => item.category === category);
  }, [category, items]);

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {categories.map((tab) => {
          const active = category === tab;

          return (
            <button
              key={tab}
              type="button"
              aria-pressed={active}
              onClick={() => setCategory(tab)}
              className={cn(
                "cursor-pointer rounded-lg border px-4 py-1.5 text-sm font-medium transition-colors",
                active
                  ? "border-foreground/10 bg-white text-foreground"
                  : "border-transparent text-foreground/70 hover:text-foreground",
              )}
            >
              {tab}
            </button>
          );
        })}
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((item) => (
          <IntegrationCard key={item.id} item={item} locale={locale} />
        ))}
      </div>
    </div>
  );
}

function IntegrationCard({
  item,
  locale,
}: {
  item: IntegrationItem;
  locale: Locale;
}) {
  const className = cn(
    "service-mesh-surface flex h-full flex-col overflow-hidden rounded-2xl border border-line p-6",
    item.href && "transition-colors hover:border-foreground/15",
  );

  const content = (
    <>
      <Image
        src={imageSrc(item.logo)}
        alt={`${item.name} logo`}
        width={50}
        height={50}
        className="size-[50px] rounded-full bg-white object-contain"
      />
      <h3 className="mt-4 font-heading text-lg font-semibold tracking-tight text-foreground">
        {item.name}
      </h3>
      <p className="mt-2 flex-1 text-sm leading-6 text-muted-foreground">
        {item.description}
      </p>
      <span
        className={cn(
          "mt-4 inline-flex items-center gap-1 text-sm font-medium",
          item.href ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {item.cta}
        {item.href ? (
          item.external ? (
            <DirectionalExternalArrow className="size-3.5" />
          ) : (
            <DirectionalArrow className="size-4" />
          )
        ) : null}
      </span>
    </>
  );

  if (!item.href) {
    return <div className={className}>{content}</div>;
  }

  if (item.external) {
    return (
      <a
        href={item.href}
        className={className}
        {...(item.newTab
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {content}
      </a>
    );
  }

  return (
    <Link href={localizeHref(item.href, locale)} className={className}>
      {content}
    </Link>
  );
}
